import config from "./config";
import { TraceInfo } from "./actionTypes";
import { getUin } from "./util";
import { LatestTrace, ActionItem } from "./types";
import { inline as inlineStore } from "./store";
import DocumentMonitor from "./DocumentMonitor";

export interface CachePage {
    id: string;
    appid: string;
    pageid: string;
    node: Element;
    exposed: boolean; // 页面是否已经上报过曝光
    timestamp: number;
    traceinfo: TraceInfo; // 页面的trace来源信息
    exposeComplete: boolean; // 是否上报曝光完成，完成了会更新traceIndex
    visibleState: boolean; // 页面元素是否在可视区域且超过50%
    lastVisibleTime: number; // 页面最近一次变成可见时的时间
    moreInfo: Record<string, string>; // 页面元素上更多的配置信息
}

function fromSearchTrace(): TraceInfo {
    const search: string = window.location.search || "";

    const obj = {};

    search.replace(/([0-9a-z-_]+?)=(.*?)(&|$)/gi, ($0, $1, $2): string => {
        obj[$1] = decodeURIComponent($2);

        return $0;
    });

    const trace: TraceInfo = {
        trace_id: "",
        trace_detail: "",
        trace_index: "",
        trace_num: 0
    };

    trace.trace_num = parseInt(obj["traceNum"] || obj["trace_num"], 10) || 0;

    trace.trace_index = obj["traceIndex"] || obj["trace_index"] || "";

    trace.trace_id = obj["traceId"] || obj["trace_id"] || "";

    trace.trace_detail = obj["traceDetail"] || obj["trace_detail"] || "";

    try {
        // 旧版SDK会在base64前面添加前缀base64-
        trace.trace_detail = window.atob(
            trace.trace_detail.replace(new RegExp(`^${config.tracePrefix}`), "")
        );
    } catch (err) {}

    if (obj["enteranceId"]) {
        try {
            const detail = trace.trace_detail
                ? JSON.parse(trace.trace_detail)
                : {};

            detail.enteranceId = obj["enteranceId"];

            trace.trace_detail = JSON.stringify(detail);
        } catch (err) {}
    }

    return trace;
}

export function getTraceInfo(page: CachePage, item?: ActionItem): LatestTrace {
    if (typeof page === "undefined") {
        return {
            traceId: "",
            traceIndex: "",
            traceNum: 1,
            traceDetail: {
                appid: "",
                page_id: "",
                module_id: "",
                sub_module_id: "",
                item_id: "",
                item_type: "",
                rule_id: 0,
                str_ruleid: ""
            }
        };
    }

    return {
        traceId: page.traceinfo.trace_id,
        traceIndex: page.traceinfo.trace_index,
        traceNum: page.traceinfo.trace_num,
        traceDetail: {
            appid: page.appid,
            page_id: page.pageid,
            module_id: item && item.module_id,
            sub_module_id: item && item.sub_module_id,
            item_id: encodeURIComponent((item && item.item_id) || ""),
            item_type: (item && item.item_type) || "",
            rule_id: item && item.rule_id,
            str_ruleid: item && item.str_ruleid
        }
    };
}

export function encodeTraceDetail(detail: object | string): string {
    return `${config.tracePrefix}${window.btoa(
        typeof detail === "string" ? detail : JSON.stringify(detail)
    )}`;
}

function checkPageTrace(cp: CachePage): void {
    let { traceinfo } = cp;

    if (traceinfo === null || typeof traceinfo === "undefined") {
        traceinfo = {
            trace_id: "",
            trace_detail: "",
            trace_index: "",
            trace_num: 0
        };
    }

    if (
        typeof traceinfo.trace_id === "undefined" ||
        traceinfo.trace_id.length === 0
    ) {
        traceinfo.trace_id =
            String(getUin()) +
            Math.round(Date.now() / 1000) +
            Math.floor(Math.random() * 10);
    }

    if (typeof traceinfo.trace_index === "undefined") {
        traceinfo.trace_index = "";
    }

    if (
        typeof traceinfo.trace_detail === "undefined" ||
        traceinfo.trace_detail.length === 0
    ) {
        traceinfo.trace_detail = "{}";
    }

    if (!(traceinfo.trace_num >= 1)) {
        traceinfo.trace_num = 1;
    } else {
        traceinfo.trace_num += 1;
    }

    cp.traceinfo = traceinfo;
}

export class PageManager {
    private pathname = "";

    private pages: Array<CachePage> = [];

    private storageTrace: Partial<TraceInfo>;

    private documentMonitor: DocumentMonitor;

    init(): void {
        // reset pages
        this.pages = [];

        this.documentMonitor = new DocumentMonitor();

        this.pathname = window.location.pathname
            .split("/")
            .slice(0, 3)
            .join("/");

        const nodes: NodeList = document.querySelectorAll("[vt-appid]");

        nodes.forEach((node: Element): void => {
            this.addPage({
                appid: node.getAttribute("vt-appid"),
                pageid: node.getAttribute("vt-pageid") || this.pathname,
                node
            });
        });

        // html标签用来兼容旧版上报
        this.addPage({
            appid: window["APPID"] || "",
            pageid: window["PAGEID"] || this.pathname,
            node: document.documentElement
        });
    }

    set storeTrace(trace: Partial<TraceInfo>) {
        this.storageTrace = trace;
    }

    updatePageVisible(id: string | Element, visible: boolean): void {
        const page = this.getPage(id);

        if (!page) {
            return;
        }

        // 从不可见变成可见更新可见时间
        if (visible && page.visibleState === false) {
            page.lastVisibleTime = Date.now();
        }

        page.visibleState = visible;
    }

    /**
     * 当前页面是否真正可见，需要满足两个条件：1. dom是否真的在可视区域；2. 当前页面是否在前台
     * @return {boolean} [description]
     */
    isPageVisible(id: string | Element): boolean {
        const page = this.getPage(id);

        if (!page) {
            return false;
        }

        return page.visibleState && this.documentMonitor.isVisible();
    }

    isDocumentVisible(): boolean {
        return this.documentMonitor.isVisible();
    }

    // 判断一个容器是否在页面列表里
    inPageCache(el: Element): boolean {
        return (
            this.pages.findIndex((cp: CachePage): boolean => {
                return cp.node.isSameNode(el);
            }) >= 0
        );
    }

    updatePages(): void {
        const nodes: NodeList = document.querySelectorAll("[vt-appid]");

        nodes.forEach((node: Element): void => {
            if (this.inPageCache(node)) {
                return;
            }

            this.addPage({
                appid: node.getAttribute("vt-appid"),
                pageid: node.getAttribute("vt-pageid") || this.pathname,
                node
            });
        });
    }

    getUnexposePages(): Array<CachePage> {
        return this.pages.filter((cp: CachePage): boolean => {
            return cp.exposed === false;
        });
    }

    getVisiblePages(): Array<CachePage> {
        if (this.documentMonitor.isVisible() === false) {
            return [];
        }

        return this.pages.filter((cp: CachePage): boolean => cp.visibleState);
    }

    get pageNum(): number {
        return this.pages.length;
    }

    private findPage(id: string | Element): CachePage {
        return this.pages.find((p: CachePage): boolean => {
            return typeof id === "string" ? p.id === id : p.node.isSameNode(id);
        });
    }

    getPage(id: string | Element): CachePage {
        const page: CachePage = this.findPage(id);

        if (page) {
            return page;
        }

        // 没有找到就重新更新下，有可能是动态创建的
        this.updatePages();

        return this.findPage(id);
    }

    /**
     * 重新更新缓存里的某个page
     * @param  {string | Element}     id [description]
     * @return {CachePage}   [description]
     */
    recachePage(id: string | Element): CachePage {
        const oriPage = this.findPage(id);

        this.pages = this.pages.filter((cp: CachePage): boolean =>
            typeof id === "string" ? cp.id !== id : !cp.node.isSameNode(id)
        );

        const newPage = this.getPage(id);

        const idx = this.pages.findIndex(cp =>
            cp.node.isSameNode(oriPage.node)
        );

        if (idx >= 0 && oriPage) {
            this.pages.splice(
                idx,
                1,
                Object.assign(oriPage, {
                    traceinfo: newPage.traceinfo,
                    moreInfo: newPage.moreInfo
                })
            );
        }

        return newPage;
    }

    // 更新页面trace信息
    updateTrace(id: string, trace: TraceInfo): void {
        const cp: CachePage = this.getPage(id);

        if (cp) {
            cp.traceinfo = trace;
        }
    }

    // 更新页面已经曝光过
    updateExposed(id: string | Element): void {
        const cp: CachePage = this.getPage(id);

        if (cp) {
            cp.exposed = true;
        }
    }

    // 新增一个页面
    addPage({
        appid,
        pageid,
        node
    }: {
        appid: string;
        pageid: string;
        node: Element;
    }): CachePage {
        const cp: CachePage = {
            id: String(Date.now() + Math.floor(Math.random() * 1000)),
            appid,
            pageid,
            node,
            exposed: false,
            traceinfo: null,
            timestamp: Date.now(),
            exposeComplete: false,
            visibleState: false,
            lastVisibleTime: Date.now(),
            moreInfo: {}
        };

        // 给一个默认appid，不然会上报一个空的appid，为了方便追踪，直接用当前域名
        if (!cp.appid) {
            cp.appid = window.location.hostname;
        }

        Object.values(node.attributes).forEach(attr => {
            if (attr.name?.startsWith("vt-")) {
                const name = attr.name.substring(3);

                cp.moreInfo[name] = attr.value;
            }
        });

        const info: string = node.getAttribute("vt-trace");
        const samePageClick: Partial<TraceInfo> = inlineStore.getItem();

        // 标注trace信息
        if (info) {
            try {
                cp.traceinfo = JSON.parse(info);
            } catch (err) {}

            // page有可能是由当前页面的点击操作触发新建的，这个时候的trace应该是点击item
        } else if (Object.keys(samePageClick).length > 0) {
            cp.traceinfo = {
                trace_id: "",
                trace_num: 0,
                trace_detail: "",
                trace_index: "",
                ...samePageClick
            };
        } else {
            // 通过url参数传递trace信息
            cp.traceinfo = fromSearchTrace();
        }

        // 通过localstorage传递trace信息
        if (!(cp.traceinfo && cp.traceinfo.trace_index)) {
            // trace_detail需要合并，而不是直接覆盖
            const cpTraceDetail = cp.traceinfo && cp.traceinfo.trace_detail;
            const storeTraceDetail =
                this.storageTrace && this.storageTrace.trace_detail;

            let traceDetail = storeTraceDetail || cpTraceDetail;

            try {
                const cpDetail = cpTraceDetail ? JSON.parse(cpTraceDetail) : {};
                const storeDetail = storeTraceDetail
                    ? JSON.parse(storeTraceDetail)
                    : {};

                traceDetail = JSON.stringify({
                    ...cpDetail,
                    ...storeDetail
                });
            } catch (err) {}

            cp.traceinfo = {
                ...cp.traceinfo,
                ...this.storageTrace,
                trace_detail: traceDetail
            };
        }

        checkPageTrace(cp);

        this.pages.push(cp);

        return cp;
    }
}

export default new PageManager();
